// Angular
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  Injector,
  Inject,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';

// Third-party
import { Store } from '@ngrx/store';
import { Observable, of, Subject, merge, combineLatest } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import * as _ from 'lodash';
import { Node, SiteEntry } from '@alfresco/js-api';

// Alfresco
import { InfoDrawerComponent } from '@alfresco/adf-core';

// Project
import { ICommentThread } from '../../../modules/wk-document-decorator';
import { CitationService } from '../../../services/citations.service';
import { WkInfodrawerService } from '../services/wk-info-drawer.service';
import {
  getThreadCount,
  getTotalReferenceCount,
  getDecoratorLoadingCompleted,
} from '../../../modules/wk-document-decorator/state/selectors';
import { AppStore } from '../../../store';
import { SiteConfigService, WkCommentsTabService } from '../../../services';
import { getAiUMCReleaseNotes, INote } from '../../..//modules/ai-umc';

@Component({
  selector: 'wk-info-drawer-layout',
  templateUrl: './wk-info-drawer-layout.component.html',
  styleUrls: ['./wk-info-drawer-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkInfodrawerLayoutComponent
  extends InfoDrawerComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Output()
  infodrawerClosed = new EventEmitter<boolean>();
  @Output()
  isEditModeChange = new EventEmitter<boolean>();
  @Input()
  isEditMode: boolean;
  node: Node | SiteEntry;
  @Input()
  set nodeInput(node: Node | SiteEntry) {
    this.node = node;
    this.editButtonState &&
      !(
        (this.node as Node)?.isFolder ||
        (this.node as Node)?.aspectNames?.includes('cm:workingcopy')
      ) &&
      this.isEditModeChange.emit(false);
  }
  @ViewChild('tabLayout') tabLayout;
  @ViewChild('singleLayout') singleLayout;
  @ViewChild('templateContainer', { read: ViewContainerRef })
  templateContainer: ViewContainerRef;
  isViewerMode: boolean;
  selectedIndex = 0;
  editButtonState: boolean;
  protected readonly onDestroy$ = new Subject<void>();
  private totalReferences$ = null;
  component: { id: string; isFolder: true };
  public disabledStates: { [key: string]: Observable<boolean> };
  private readonly ckeditorChangeDataListener = new Subject<void>();
  private readonly aiUMCNotes$: Observable<INote[]> =
    this.store.select<INote[]>(getAiUMCReleaseNotes);
  constructor(
    private readonly router: Router,
    private readonly cdref: ChangeDetectorRef,
    private readonly injector: Injector,
    private readonly store: Store<AppStore>,
    private readonly citationService: CitationService,
    private readonly wkInfodrawerService: WkInfodrawerService,
    private readonly commentsTabService: WkCommentsTabService,
    @Inject(SiteConfigService) private readonly siteConfig: SiteConfigService
  ) {
    super();
    this.store = this.injector.get<Store<AppStore>>(Store);
  }

  ngOnInit() {
    this.setEditButtonState();
    this.showHeader = false;
    this.isViewerMode =
      this.router.url.includes('preview') ||
      this.router.url.includes('edit') ||
      this.router.url.includes('compare') ||
      this.router.url.includes('merge');

    this.store
      .select<boolean>(getDecoratorLoadingCompleted)
      .pipe(debounceTime(500), takeUntil(this.onDestroy$))
      .subscribe((isLoaded: boolean) => {
        if (isLoaded) {
          this.createChangeEventListener();
        }
      });
    this.citationService.changePanel$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((index) => {
        this.selectedIndex = index;
      });

    this.wkInfodrawerService
      .getCancelEvent()
      .pipe(
        filter(() => !this.isViewerMode),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.isEditModeChange.emit(false));

    this.wkInfodrawerService.nodeUpdated
      .pipe(
        filter(() => !this.isViewerMode),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.isEditModeChange.emit(false));
    this.totalReferences$ = this.store.select(getTotalReferenceCount);

    merge(
      this.citationService.isInlineToggled$,
      this.ckeditorChangeDataListener.pipe(debounceTime(100))
    )
      .pipe(
        switchMap(this.enableTabLayout.bind(this)),
        takeUntil(this.onDestroy$)
      )
      .subscribe((disabledStates) => {
        this.disabledStates = disabledStates;
      });

    this.citationService.activeAnnotationListener
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        !isNaN(value) && (this.selectedIndex = value);
      });
  }

  private enableTabLayout(value): Observable<{
    'Cross-links': Observable<boolean>;
    Comments: Observable<boolean>;
  }> {
    const commentsObservable$ = value
      ? this.commentThreadCount().pipe(
          takeUntil(this.onDestroy$),
          map((threadCount) => threadCount === 0)
        )
      : combineLatest([
          this.store.select(getThreadCount),
          this.store.select(getAiUMCReleaseNotes),
        ]).pipe(
          takeUntil(this.onDestroy$),
          map(([threadCount, releaseNotes]) =>
            Boolean(threadCount === 0 && releaseNotes.length === 0)
          )
        );
    return of({
      'Cross-links': this.totalReferences$.pipe(
        map((refCount) => refCount === 0),
        takeUntil(this.onDestroy$)
      ),
      Comments: commentsObservable$,
    });
  }

  private createChangeEventListener(): void {
    this.citationService?.ckeditorInstanceFromEditorComp?.model?.document?.on?.(
      'change:data',
      () => {
        this.citationService.isInlineToggledSubject.next(
          this.commentsTabService.isInlineToggled
        );
      },
      { priority: 'highest' }
    );
  }

  public onTabChange(index): void {
    if (index !== this.selectedIndex) {
      this.citationService.changeSidePanelTabTo(index);
    }
    if (index === 0) {
      this.citationService.currentSelectedCitationDoc = '';
    }
  }

  onSelectedCitation() {
    if (this.citationService.currentSelectedCitationDoc) {
      this.citationService.selectedCitationDoc.next({
        citationId: this.citationService.currentSelectedCitationDoc,
      });
      this.citationService.currentSelectedCitationDoc = '';
    }
    if (this.selectedIndex === 2) {
      this.citationService.isOpenCommentsTab.next(true);
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.citationService.currentSelectedCitationDoc = '';
  }

  hideInfodrawer() {
    this.infodrawerClosed.emit();
  }

  ngAfterViewInit() {
    this.templateContainer.createEmbeddedView(
      this.showTabLayout() ? this.tabLayout : this.singleLayout
    );
    this.cdref.detectChanges();
  }

  cancelAllChanges() {
    this.wkInfodrawerService.cancelAllMetadataChanges();
    this.isEditModeChange.emit(false);
  }

  // should be updated to support editing of metadata for checked in documents.
  public canEditMetadata(): boolean {
    return (
      !this.isEditMode && this.editButtonState && (this.node as Node)?.isFolder
    );
  }

  private setEditButtonState() {
    const currentSite = localStorage.getItem('currentSite');
    const currSiteObj = this.siteConfig.getCurrentSiteObject(currentSite);
    this.editButtonState &&
      !(this.node as Node)?.isFolder &&
      this.isEditModeChange.emit(false);
    this.editButtonState = currSiteObj?.editButtonOnFileLibraryLevel ?? false;
  }

  /**
   * Calculates the total number of unresolved comment threads from both
   * 'comment' and 'releasenotes' repositories, and combines it with the
   * thread count from the store.
   *
   * @returns Observable<number> - The total unresolved thread count.
   */
  private commentThreadCount(): Observable<number> {
    const commentThreads = this.omitResolvedThreads(
      this.citationService.getCommentsInRepository('comment')
    );
    const releaseNotesThreads = this.omitResolvedThreads(
      this.citationService.getCommentsInRepository('releasenotes')
    );
    const totalThreadCount = commentThreads.length + releaseNotesThreads.length;
    return this.aiUMCNotes$.pipe(
      takeUntil(this.onDestroy$),
      map((aiReleaseNotes = []) => totalThreadCount + aiReleaseNotes.length)
    );
  }

  /**
   * Filters out resolved threads from the provided list.
   *
   * @param totalThreads - Array of thread objects to filter.
   * @returns ICommentThread[] - Array of unresolved threads.
   */
  public omitResolvedThreads(totalThreads: ICommentThread[]): ICommentThread[] {
    return _.filter(
      totalThreads,
      (thread) => thread.comments?.[0]?.attributes?.status !== 'resolve'
    );
  }
}
